import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { useEffect } from "react";
import { ShieldAlert } from "lucide-react";

type UserRole = "user" | "admin" | "manager" | "crew" | "subcontractor";

interface RoleGuardProps {
  allowedRoles: UserRole[];
  children: React.ReactNode;
}

/**
 * RoleGuard restricts access to pages based on user role.
 * If the user's role is not in the allowedRoles list, they see an access denied message.
 */
export function RoleGuard({ allowedRoles, children }: RoleGuardProps) {
  const { user, loading } = useAuth();
  const [, setLocation] = useLocation();

  if (loading) return null;

  // Admin always has access
  if (user?.role === "admin") return <>{children}</>;

  // "user" role is treated as admin (owner) for backwards compatibility
  if (user?.role === "user") return <>{children}</>;

  if (!user || !allowedRoles.includes(user.role as UserRole)) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] text-center p-6">
        <ShieldAlert className="h-16 w-16 text-muted-foreground/30 mb-4" />
        <h2 className="text-xl font-semibold mb-2">Access Restricted</h2>
        <p className="text-muted-foreground max-w-md">
          You don't have permission to view this page. Contact your administrator
          if you believe this is an error.
        </p>
        <button
          onClick={() => setLocation("/")}
          className="mt-6 text-primary hover:underline text-sm"
        >
          Go to Dashboard
        </button>
      </div>
    );
  }

  return <>{children}</>;
}

/**
 * Role hierarchy helpers
 */
export const FINANCIAL_ROLES: UserRole[] = ["admin", "manager", "user"];
export const CREW_ROLES: UserRole[] = ["admin", "manager", "crew"];
export const SUB_ROLES: UserRole[] = ["admin", "manager", "subcontractor"];
export const ALL_INTERNAL_ROLES: UserRole[] = ["admin", "manager", "crew"];

/**
 * Determine which sidebar items a role can see
 */
export function getVisibleMainItems(role: string) {
  switch (role) {
    case "crew":
      return ["Dashboard", "Projects", "Daily Reports", "Receipts"];
    case "subcontractor":
      return ["Dashboard", "My Bills", "My Payments", "My Projects"];
    default:
      return null; // null = show all
  }
}

export function getVisibleGroups(role: string) {
  switch (role) {
    case "crew":
      return []; // No groups visible for crew
    case "subcontractor":
      return []; // No groups visible for subs
    default:
      return null; // null = show all
  }
}
